// NotePhotoUploader - Drag-Drop Foto-Upload für Logbuch-Einträge

import React, { useState, useRef, useCallback } from 'react';
import { Camera, X, Upload, Loader2, AlertCircle } from 'lucide-react';
import { uploadNotePhotoWithThumbnail, validateImageFile } from '../../services/storageService';
import type { PhotoMetadata } from '../../types';

interface NotePhotoUploaderProps {
  userId: string;
  noteId?: string;
  photos: PhotoMetadata[];
  onChange: (photos: PhotoMetadata[]) => void;
  maxPhotos?: number;
}

interface UploadingPhoto {
  id: string;
  file: File;
  preview: string;
  progress: number;
  error?: string;
}

export function NotePhotoUploader({
  userId,
  noteId,
  photos,
  onChange,
  maxPhotos = 10,
}: NotePhotoUploaderProps) {
  const [uploading, setUploading] = useState<UploadingPhoto[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canAddMore = photos.length + uploading.length < maxPhotos;

  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const filesToUpload = Array.from(files).slice(0, maxPhotos - photos.length - uploading.length);

      // Validate and create previews
      const newUploading: UploadingPhoto[] = [];
      for (const file of filesToUpload) {
        const error = validateImageFile(file);
        const preview = URL.createObjectURL(file);
        newUploading.push({
          id: `${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
          file,
          preview,
          progress: 0,
          error: error || undefined,
        });
      }

      setUploading((prev) => [...prev, ...newUploading]);

      // Upload files that passed validation
      for (const item of newUploading) {
        if (item.error) continue;

        try {
          const result = await uploadNotePhotoWithThumbnail(item.file, userId, noteId);

          // Add to photos
          const newPhoto: PhotoMetadata = {
            url: result.url,
            thumbnailUrl: result.thumbnailUrl,
          };

          onChange([...photos, newPhoto]);

          // Remove from uploading
          setUploading((prev) => prev.filter((u) => u.id !== item.id));
          URL.revokeObjectURL(item.preview);
        } catch (error) {
          console.error('Upload error:', error);
          setUploading((prev) =>
            prev.map((u) =>
              u.id === item.id
                ? { ...u, error: error instanceof Error ? error.message : 'Upload fehlgeschlagen' }
                : u
            )
          );
        }
      }
    },
    [photos, uploading.length, maxPhotos, userId, noteId, onChange]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFileSelect(e.target.files);
      // Reset input so same file can be selected again
      e.target.value = '';
    },
    [handleFileSelect]
  );

  const removePhoto = useCallback(
    (index: number) => {
      const newPhotos = [...photos];
      newPhotos.splice(index, 1);
      onChange(newPhotos);
    },
    [photos, onChange]
  );

  const removeUploading = useCallback((id: string) => {
    setUploading((prev) => {
      const item = prev.find((u) => u.id === id);
      if (item) URL.revokeObjectURL(item.preview);
      return prev.filter((u) => u.id !== id);
    });
  }, []);

  return (
    <div className="space-y-3">
      {/* Foto-Grid */}
      {(photos.length > 0 || uploading.length > 0) && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {/* Hochgeladene Fotos */}
          {photos.map((photo, index) => (
            <div
              key={photo.url}
              className="relative aspect-square rounded-lg overflow-hidden bg-gray-100"
            >
              <img
                src={photo.thumbnailUrl || photo.url}
                alt={`Foto ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => removePhoto(index)}
                className="absolute top-1 right-1 p-1 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}

          {/* Uploading Fotos */}
          {uploading.map((item) => (
            <div
              key={item.id}
              className="relative aspect-square rounded-lg overflow-hidden bg-gray-100"
            >
              <img
                src={item.preview}
                alt="Wird hochgeladen..."
                className="w-full h-full object-cover opacity-50"
              />
              {item.error ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-500/20 p-2">
                  <AlertCircle className="w-6 h-6 text-red-500 mb-1" />
                  <span className="text-xs text-red-700 text-center">{item.error}</span>
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                </div>
              )}
              <button
                type="button"
                onClick={() => removeUploading(item.id)}
                className="absolute top-1 right-1 p-1 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload-Bereich */}
      {canAddMore && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all
            ${isDragging
              ? 'border-indigo-500 bg-indigo-50'
              : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
            }
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleInputChange}
            className="hidden"
          />
          <div className="flex flex-col items-center gap-2">
            {isDragging ? (
              <>
                <Upload className="w-8 h-8 text-indigo-500" />
                <span className="text-sm text-indigo-600 font-medium">
                  Fotos hier ablegen
                </span>
              </>
            ) : (
              <>
                <Camera className="w-8 h-8 text-gray-400" />
                <span className="text-sm text-gray-600">
                  Fotos hinzufügen
                </span>
                <span className="text-xs text-gray-400">
                  Klicken oder hierher ziehen (max. {maxPhotos} Fotos)
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Hinweis wenn Maximum erreicht */}
      {!canAddMore && (
        <p className="text-xs text-amber-600 text-center">
          Maximum von {maxPhotos} Fotos erreicht
        </p>
      )}
    </div>
  );
}

export default NotePhotoUploader;
